import { useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { MessageTrainerDialog } from '@/features/OrgLayout/batchDetail/MessageTrainerDialog';
import type { OrgSessionDetail, OrgSessionAttendanceRow, OrgTrainer } from '@/lib/api/org';

const STATUS_STYLES: Record<OrgSessionAttendanceRow['status'], string> = {
  present: 'bg-[#f0fdf4] text-[#16a34a]',
  late: 'bg-[#fffbeb] text-[#d97706]',
  absent: 'bg-[#fef2f2] text-[#dc2626]',
  not_marked: 'bg-[#f3f4f6] text-[#6b7280]',
};

const STATUS_LABELS: Record<OrgSessionAttendanceRow['status'], string> = {
  present: 'Present',
  late: 'Late',
  absent: 'Absent',
  not_marked: 'Not marked',
};

const TYPE_LABELS: Record<OrgSessionDetail['session_type'], string> = {
  live_class: 'Live Class',
  recorded: 'Recorded',
  office_hours: 'Office Hours',
  lab: 'Lab',
};

function AttendanceBadge({ status }: { status: OrgSessionAttendanceRow['status'] }) {
  return <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-xs font-medium', STATUS_STYLES[status])}>{STATUS_LABELS[status]}</span>;
}

export function SessionDetailView({ data, batchId, trainers }: { data: OrgSessionDetail; batchId: string; trainers: OrgTrainer[] }) {
  const [remindOpen, setRemindOpen] = useState(false);
  const hasUnmarked = data.attendance.some((a) => a.status === 'not_marked');

  const attendanceReminderPrefill = useMemo(
    () => ({
      reason: 'attendance' as const,
      body: `Attendance is missing for "${data.title}"${data.scheduled_at ? ` (${data.scheduled_at})` : ''} — can you update it?`,
    }),
    [data.title, data.scheduled_at],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[28px] font-bold leading-tight text-[#111827]">{data.title}</h1>
        <span className="inline-flex items-center rounded-full bg-[#f3f4f6] px-2.5 py-0.5 text-xs font-medium text-[#6b7280]">
          {TYPE_LABELS[data.session_type]}
        </span>
        {hasUnmarked && (
          <button
            type="button"
            onClick={() => setRemindOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#ff5a1f] hover:bg-[#fff1ec]"
          >
            <MessageSquare className="h-4 w-4" /> Remind trainer
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#6b7280]">
        {data.scheduled_at && <span>Scheduled {data.scheduled_at}</span>}
        <span>Status {data.status}</span>
      </div>

      <OrgCard className="overflow-hidden">
        {data.attendance.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9ca3af]">No students enrolled in this batch.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="border-b border-[#ececec]">
                  {['Student', 'Status', 'Joined at'].map((h, i) => (
                    <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.attendance.map((s, i) => (
                  <tr key={s.user_id} className={cn(i !== data.attendance.length - 1 && 'border-b border-[#ececec]')}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <OrgAvatar name={s.name} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                          <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><AttendanceBadge status={s.status} /></td>
                    <td className="px-6 py-4 text-sm text-[#374151]">{s.joined_at || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </OrgCard>

      <MessageTrainerDialog
        batchId={batchId}
        trainers={trainers}
        open={remindOpen}
        onOpenChange={setRemindOpen}
        prefill={attendanceReminderPrefill}
      />
    </div>
  );
}
